/**
 * The rules the email screens run on.
 *
 * An account's addresses are one primary and any number of secondaries, and almost every rule here
 * comes from what the primary is for: it is where account mail — password resets above all — is
 * sent. So the API refuses to promote an address that has not been confirmed, refuses to delete the
 * one holding the role, and confirms an address only against the code it mailed there. The
 * predicates below exist to keep the interface from offering an action that would only come back as
 * a 422.
 *
 * Two of those refusals are worth reading together, because between them they are the only way to
 * retire a primary address: `promote` moves the role onto another confirmed address, demoting the
 * one that held it, and the demoted address is then an ordinary secondary that `destroy` will take.
 * That is why removal is disabled rather than hidden on the primary row — the way out is a step
 * away, not unavailable.
 *
 * A confirmation code lives for an hour and there is no route to reissue one, so an address whose
 * code has gone stale is removed and added again rather than resent.
 */
import {formatDate} from './data'

export const PRIMARY = 'primary'
export const SECONDARY = 'secondary'

export const VERIFY = 'verify'
export const PROMOTE = 'promote'

export const isPrimary = (email = {}) => email.category === PRIMARY

export const isVerified = (email = {}) => Boolean(email.confirmedAt)

export const primaryEmail = (emails = []) => emails.find(isPrimary) || {}

export const verifiedSecondaries = (emails = []) => emails.filter(email => !isPrimary(email) && isVerified(email))

// Verifying or promoting an address moves it to the end of the reducer's collection, which would
// otherwise make a row jump down the list the moment it was acted on.
export const orderedEmails = (emails = []) => [...emails].sort(
  (first, second) => (
    (isPrimary(second) - isPrimary(first)) || (new Date(first.createdAt) - new Date(second.createdAt))
  )
)

export const isRemovalRefused = (email = {}) => isPrimary(email)

/**
 * The chip carries what the row's own lines cannot say for themselves. A verified secondary is the
 * unremarkable case and gets none — its second line already reads "Verified …", and a chip repeating
 * that would only crowd out the two states worth spotting from across the list.
 */
export const emailChip = (email = {}) => {
  if (isPrimary(email))
    return {label: 'Primary', color: 'primary'}
  else if (!isVerified(email))
    return {label: 'Unverified', color: 'warning'}
  else
    return null
}

export const emailDetail = (email = {}) => (
  isVerified(email) ?
    `Verified ${formatDate(email.confirmedAt)}` :
    `Added ${formatDate(email.createdAt)} · the code expires an hour after it is sent`
)

export const emailAction = (email = {}) => {
  if (isPrimary(email))
    return null
  else if (isVerified(email))
    return {label: 'Make primary', mode: PROMOTE}
  else
    return {label: 'Enter code', mode: VERIFY}
}

export const removalPrompt = ({email, emails = []}) => {
  const primary = primaryEmail(emails)
  const destination = primary.address ? ` Account mail keeps going to ${primary.address}.` : ''
  const lastFallback = isVerified(email) && verifiedSecondaries(emails).length === 1

  return {
    body: `It stops being an address on your account straight away.${destination}`,
    warning: lastFallback ?
      'This is your only other verified address — without it, the primary is the only place we can reach you.' :
      ''
  }
}

export const promotionPrompt = (emails = []) => {
  const primary = primaryEmail(emails)

  return {
    body: 'Account mail — password resets above all — will go there from now on.',
    detail: primary.address ?
      `${primary.address} stays on your account as a verified second address, so nothing is lost by swapping them back later.` :
      '',
    keep: primary.address ? `Keep ${primary.address}` : 'Cancel'
  }
}
